import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import { usePlayerProfile } from '../contexts/PlayerProfileContext';
import { usePlayerPopover } from '../contexts/PlayerPopoverContext';
import TeamLogo from './TeamLogo';
import PlayerRow from './PlayerRow';
import LineupDiamond from './LineupDiamond';
import PlayerSilhouette from './PlayerSilhouette';
import { TrophyIcon, CogIcon, HistoryIcon, SwapIcon, SubtractIcon } from './icons/StreamlineFlexSolid';
import TradeModal from './TradeModal';
import { computeStandings } from '../utils/standingsUtils';
import { buildFallbackMlbGames, getPlayerGameStatus } from '../services/mlbGameStatus';
import {
  getRosterForDate,
  saveRosterForDate,
  getAvailableDates,
  copyRosterToDate,
  addPlayerToRoster,
  removePlayerFromRoster,
  removePlayerFromLineup,
  movePlayerToBench,
  movePlayerToLineupPosition,
  movePlayerToMinorLeague,
  movePlayerFromMinorLeague,
  isPositionEligible,
  getEligibleLineupPositions,
  getRosterSummary,
  validateActiveLineup,
  updateLineupForAllFutureDates,
  isRosterLocked,
  getWeekStart,
  getWeekEnd,
  ACTIVE_LINEUP_REQUIREMENTS,
  POSITIONS,
  MAX_ROSTER_SIZE,
  MAX_MINOR_LEAGUE_SIZE,
  getCurrentDate,
  getPitchers,
  getOutfielders,
  getUtility
} from '../utils/rosterManager';
import { constrainDateToSeason } from '../utils/seasonManager';
import draftService from '../services/draftService';
import fantasyDataService from '../services/fantasyDataService';
import PlayerTableCell from './PlayerTableCell';
import { useTestDataBanner } from '../contexts/TestDataBannerContext';
import { USE_ROSTER_FIXTURES, buildSampleNewsForPlayers } from '../services/rosterFixtures';

const NEWS_TAG_LABEL = { injury: 'Injury', lineup: 'Role Change', transaction: 'Transaction' };

function RosterTables({ team: teamProp = null, readOnly = false } = {}) {
  const { league, userTeam, currentMatchup, weekMatchups, userTeamStanding: contextUserTeamStanding, onOpenTeamSettings } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const leagueId = league?.id || league?.leagueId;
  const userId = user?.userId || user?.id || user?.sub;
  const userTeamId = userTeam?.id ?? userTeam?.teamId;
  // Global season-phase test-data mode (pre-draft / mid-season / after-season).
  const { mode: phase } = useTestDataBanner();
  // Today's MLB games for this phase — drives the per-player Game column (opponent
  // + time / live score & inning), consistent with Live Matchup.
  const mlbGames = React.useMemo(() => buildFallbackMlbGames(phase), [phase]);
  // The team whose roster is shown: the logged-in user's team by default, or a
  // specific team in read-only mode (viewing an opponent's roster). All roster
  // loading, the franchise header, and standings key off the "view" team.
  const viewTeam = teamProp || userTeam;
  const viewTeamId = viewTeam?.id ?? viewTeam?.teamId;
  const viewOwnerId = teamProp ? (teamProp.owner ?? teamProp.ownerId ?? teamProp.userId ?? teamProp.id) : userId;
  // The viewed team's current matchup — the logged-in user's on My Roster, or the
  // opponent's on a team page — picked from the shared weekMatchups list.
  const viewMatchup = React.useMemo(
    () => (weekMatchups || []).find((m) => (m.team1.id || m.team1.teamId) === viewTeamId || (m.team2.id || m.team2.teamId) === viewTeamId) || (!readOnly ? currentMatchup : null),
    [weekMatchups, viewTeamId, readOnly, currentMatchup],
  );
  // Standing for the viewed team. Reuse the context value for the user's own
  // team (unless we're viewing someone else), otherwise compute it.
  const userTeamStanding = phase === 'pre-draft' ? null : (!readOnly && contextUserTeamStanding) ? contextUserTeamStanding : (league ? (() => {
    const { standings, isHeadToHead, isRotisserie, isPointsBased } = computeStandings(league);
    if (!standings?.length) return null;
    const idx = standings.findIndex((s) => (s.id || s.teamId) === viewTeamId || s.owner === viewOwnerId);
    if (idx === -1) return null;
    const entry = standings[idx];
    return { rank: idx + 1, totalTeams: standings.length, wins: entry.wins ?? 0, losses: entry.losses ?? 0, ties: entry.ties ?? 0, rotoPoints: entry.rotoPoints, points: entry.points, isHeadToHead, isRotisserie, isPointsBased };
  })() : null);
  const { openPlayerProfile } = usePlayerProfile();
  const viewFormat = 'table'; // Diamond view removed — roster is table-only.
  
  // Get league season information
  const leagueSeason = league.season || {
    startDate: league.seasonStartDate || '2026-03-26',
    endDate: league.seasonEndDate || '2026-09-27',
    year: league.seasonYear || 2026
  };
  
  // Initialize date constrained to season
  const initialDate = constrainDateToSeason(getCurrentDate(), leagueSeason);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [roster, setRoster] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  // Bumped by the season-phase switcher to force a roster reload.
  const [rosterReloadKey, setRosterReloadKey] = useState(0);
  const [viewMode, setViewMode] = useState('stats'); // 'stats', 'research', 'news', 'schedule'
  const [statsRange, setStatsRange] = useState('today'); // stat periods: today, yesterday, 7days, ...
  const [researchSubTab, setResearchSubTab] = useState('rating'); // 'rating', 'ownership', 'saber'
  const [scheduleSubTab, setScheduleSubTab] = useState('week'); // 'week', 'full'
  // Below this width the roster rows reflow (iOS-style): slot + player on top, the
  // data (game/salary/contract/stats) as a horizontal-scroll strip beneath.
  const [isNarrowRoster, setIsNarrowRoster] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 700px)');
    const onChange = (e) => setIsNarrowRoster(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);
  const [tradeMenu, setTradeMenu] = useState(null); // { player, x, y }
  const [tradeModal, setTradeModal] = useState(null); // { partnerTeam, initialMyPlayers }
  const [pendingDrop, setPendingDrop] = useState(null); // player awaiting drop confirmation
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notification, setNotification] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [rosterNews, setRosterNews] = useState([]);
  const [rosterNewsLoading, setRosterNewsLoading] = useState(false);
  const [expandedNewsId, setExpandedNewsId] = useState(null);
  const [showPlayerNews, setShowPlayerNews] = useState(typeof window !== 'undefined' && window.innerWidth >= 1050);

  // Franchise logo sizing: keep the (square) team logo exactly as tall as the
  // stacked name / owner / buttons column beside it. Measured rather than done
  // in pure CSS because a stretched flex item can't transfer its height to a
  // square width via aspect-ratio (main size resolves from content first).
  const franchiseTextRef = useRef(null);
  const [franchiseLogoSize, setFranchiseLogoSize] = useState(70.88);
  useLayoutEffect(() => {
    const el = franchiseTextRef.current;
    if (!el) return undefined;
    const measure = () => setFranchiseLogoSize(el.offsetHeight || 70.88);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewTeam]);

  // Show Player News only when viewport is wide enough (roster max 700px + gap 24 + news 320 ≈ 1050px)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1050px)');
    setShowPlayerNews(mql.matches);
    const handler = (e) => setShowPlayerNews(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Get roster period setting from league
  const rosterPeriod = league?.settings?.rosterSettings?.rosterPeriod || 'daily';
  const rosterLockStatus = isRosterLocked(rosterPeriod, currentDate);

  // The global season-phase mode (test-data banner) decides this team's roster
  // shape via resolveVariationKey: pre-draft = undrafted/empty, otherwise a full
  // drafted roster. setTestDataMode clears the fixture cache, so reload to rebuild.
  useEffect(() => {
    if (!USE_ROSTER_FIXTURES) return;
    setRosterReloadKey((k) => k + 1);
  }, [phase]);

  const SECTION_BG = {
    active: '#ffffff',
    bench: '#e9ecef',
    minorLeague: '#e8f5e9',
    il: '#ffebee'
  };
  const STAT_FONT_SIZE = '12px';

  const getPlayerFromPosition = (pos) => {
    if (['P', 'OF', 'UTIL'].includes(pos)) return selectedPlayer;
    return roster?.activeLineup?.[pos] ?? null;
  };
  const getTargetPlayer = (pos, override = null) => override ?? (['P', 'OF', 'UTIL'].includes(pos) ? null : roster?.activeLineup?.[pos] ?? null);

  const isValidSwapTarget = (targetPos, targetPlayerOverride = null) => {
    if (!selectedPosition || !roster?.activeLineup) return false;
    if (targetPos === selectedPosition) return true;

    const selPlayer = getPlayerFromPosition(selectedPosition);
    const targetPlayer = getTargetPlayer(targetPos, targetPlayerOverride);

    if (!selPlayer) {
      return targetPlayer && isPositionEligible(targetPlayer.position, selectedPosition);
    }
    if (!targetPlayer) {
      return isPositionEligible(selPlayer.position, targetPos);
    }
    const selectedCanMoveToTarget = isPositionEligible(selPlayer.position, targetPos);
    const targetCanMoveToSelected = isPositionEligible(targetPlayer.position, selectedPosition);
    
    // Valid if we can do a full swap OR if at least one player can move (other goes to bench)
    return selectedCanMoveToTarget || targetCanMoveToSelected;
  };

  // Schedule view: week = 7 days, full = 21 days from current
  const getScheduleDays = (count) => {
    const days = [];
    const baseDate = new Date(currentDate);
    for (let i = 0; i < count; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      days.push(date);
    }
    return days;
  };
  const scheduleDays = scheduleSubTab === 'full' ? getScheduleDays(21) : getScheduleDays(7);
  const scheduleColSpan = 4 + scheduleDays.length; // Pos, Drop, Player, Game + day columns
  const trailingColCount = (mode) => (mode === 'stats' ? 6 : mode === 'research' ? 5 : mode === 'news' ? 1 : scheduleDays.length);

  // Format date as MM/DD
  const formatDateHeader = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  };
  
  // Handle notification display
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Migrate legacy 'lastyear' to first previous year
  useEffect(() => {
    if (statsRange === 'lastyear') {
      const y = (league?.season?.year || league?.seasonYear || 2026) - 1;
      setStatsRange(`year${y}`);
    }
  }, [statsRange, league?.season?.year, league?.seasonYear]);

  // Load roster data when draft is completed, sync any missing draft picks onto roster
  useEffect(() => {
    const loadRosterData = async () => {
      const rosterData = await getRosterForDate(viewOwnerId, leagueId, currentDate, league, viewTeamId);
      setRoster(rosterData);
      const dates = await getAvailableDates(userId, leagueId, league);
      setAvailableDates(dates);

      // If draft is completed, ensure all my draft picks are on my roster (catches missed real-time sync)
      const draft = await draftService.getDraft(leagueId).catch(() => null);
      const myTeam = league?.teams?.find((t) => t.owner === userId);
      const myTeamId = myTeam?.id ?? myTeam?.teamId;
      if (!readOnly && draft?.status === 'completed' && myTeamId != null && (draft.picks?.length ?? 0) > 0) {
        const myPicks = (draft.picks || []).filter((p) => String(p.teamId) === String(myTeamId));
        const rosterPlayerIds = new Set((rosterData?.players || []).map((p) => String(p.id)));
        let updated = { ...rosterData };
        for (const pick of myPicks) {
          if (!pick.playerId) continue;
          if (rosterPlayerIds.has(String(pick.playerId))) continue;
          const player = {
            id: pick.playerId,
            name: pick.playerName || 'Unknown',
            position: pick.playerPosition || 'UTIL',
            team: pick.playerTeam || 'FA',
            stats: { battingAverage: 0, homeRuns: 0, rbi: 0, stolenBases: 0 }
          };
          const res = addPlayerToRoster(updated, player);
          if (res.success) {
            updated = res.roster;
            rosterPlayerIds.add(String(pick.playerId));
          }
        }
        if (updated.players?.length !== (rosterData?.players?.length ?? 0)) {
          setRoster(updated);
          await saveRosterForDate(userId, leagueId, updated, currentDate, league, userTeamId);
        }
      }
    };
    loadRosterData();
  }, [userId, viewOwnerId, viewTeamId, readOnly, leagueId, currentDate, league, rosterReloadKey]);

  // Save roster when it changes (never for a read-only opponent view)
  useEffect(() => {
    if (readOnly || !roster || !userId || !leagueId) return;
    saveRosterForDate(userId, leagueId, roster, currentDate, league, userTeamId).catch((e) => {
      console.error('Save roster failed:', e);
      setNotification({
        type: 'error',
        message: 'Failed to save roster: ' + (e.message || 'Unknown error')
      });
    });
  }, [roster, userId, leagueId, currentDate, league, userTeamId]);

  // Load news for all roster players (merged, most recent first)
  useEffect(() => {
    if (!roster?.players?.length) {
      setRosterNews([]);
      return;
    }
    // Local-fixtures mode: use sample news derived from the roster (no API).
    if (USE_ROSTER_FIXTURES) {
      setRosterNews(buildSampleNewsForPlayers(roster.players));
      setRosterNewsLoading(false);
      return;
    }
    let cancelled = false;
    const playerIds = [...new Set(roster.players.map((p) => p.id).filter(Boolean))];
    const playerMap = new Map(roster.players.map((p) => [String(p.id), p.name || 'Unknown']));
    setRosterNewsLoading(true);
    (async () => {
      try {
        const limit = Math.min(playerIds.length, 15);
        const results = await Promise.allSettled(
          playerIds.slice(0, limit).map((id) => fantasyDataService.getNewsByPlayer(id))
        );
        if (cancelled) return;
        const all = [];
        results.forEach((res, i) => {
          if (res.status !== 'fulfilled' || !Array.isArray(res.value)) return;
          const pid = playerIds[i];
          const playerName = playerMap.get(String(pid)) || 'Unknown';
          (res.value || []).slice(0, 5).forEach((item, j) => {
            all.push({
              ...item,
              id: `news-${pid}-${i}-${j}-${(item.Updated || '').toString().slice(0, 20)}`,
              playerName,
              playerId: pid
            });
          });
        });
        all.sort((a, b) => new Date(b.Updated || 0) - new Date(a.Updated || 0));
        setRosterNews(all.slice(0, 25));
      } catch (e) {
        if (!cancelled) setRosterNews([]);
      } finally {
        if (!cancelled) setRosterNewsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roster]);

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPlayer.name || !newPlayer.position || !newPlayer.team) {
      setError('Please fill in all required fields');
      return;
    }

    const player = {
      id: Date.now(),
      ...newPlayer,
      stats: {
        ...newPlayer.stats,
        battingAverage: parseFloat(newPlayer.stats.battingAverage) || 0,
        homeRuns: parseInt(newPlayer.stats.homeRuns) || 0,
        rbi: parseInt(newPlayer.stats.rbi) || 0,
        stolenBases: parseInt(newPlayer.stats.stolenBases) || 0
      }
    };

    const result = addPlayerToRoster(roster, player);
    if (result.success) {
      const updatedRoster = result.roster;
      setRoster(updatedRoster);

      await saveRosterForDate(userId, leagueId, updatedRoster, currentDate, league, userTeamId);

      const startDate = new Date(currentDate);
      const endDate = new Date(leagueSeason.endDate);
      const datesToUpdate = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        if (dateStr > currentDate) datesToUpdate.push(dateStr);
      }

      for (const date of datesToUpdate) {
        const futureRoster = await getRosterForDate(viewOwnerId, leagueId, date, league, viewTeamId);
        const futureResult = addPlayerToRoster(futureRoster, player);
        if (futureResult.success) {
          await saveRosterForDate(userId, leagueId, futureResult.roster, date, league, userTeamId);
        }
      }

      setNewPlayer({
        name: '',
        position: '',
        team: '',
        stats: { battingAverage: '', homeRuns: '', rbi: '', stolenBases: '' }
      });
      setShowAddForm(false);
      setNotification({
        type: 'success',
        message: `${player.name} added to roster for all ${datesToUpdate.length + 1} remaining days!`
      });
    } else {
      setNotification({ type: 'error', message: result.error });
    }
  };

  const handleRemovePlayer = (playerId) => {
    if (rosterLockStatus.locked) {
      setNotification({ type: 'error', message: 'Cannot drop players - roster is locked' });
      return;
    }
    const result = removePlayerFromRoster(roster, playerId);
    if (result.success) {
      setRoster(result.roster);
      setSuccess('Player removed from roster');
      setSelectedPlayer(null);
    } else {
      setError(result.error);
    }
  };

  // Dismiss the drop-confirmation bar with Escape.
  useEffect(() => {
    if (!pendingDrop) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPendingDrop(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingDrop]);

  // Drop is a two-step action: the button opens a floating confirm bar and the
  // actual removal only runs once the user confirms it there.
  const requestDropPlayer = (player) => {
    if (rosterLockStatus.locked) {
      setNotification({ type: 'error', message: 'Cannot drop players - roster is locked' });
      return;
    }
    setPendingDrop(player);
  };

  const confirmPendingDrop = () => {
    if (pendingDrop) handleRemovePlayer(pendingDrop.id);
    setPendingDrop(null);
  };

  // Drop, then jump to the Players page to pick up a replacement, pre-filtered to
  // the dropped player's primary position.
  const dropAndAddReplacement = () => {
    const pos = pendingDrop?.position || (pendingDrop?.positions && pendingDrop.positions[0]) || '';
    confirmPendingDrop();
    navigate(`/league/${leagueId}/players${pos ? `?add=${encodeURIComponent(pos)}` : ''}`);
  };

  const handleMoveToMinorLeague = (playerId) => {
    // Check if roster moves are locked
    if (rosterLockStatus.locked) {
      setNotification({
        type: 'error',
        message: 'Cannot move players - roster is locked'
      });
      return;
    }
    
    const result = movePlayerToMinorLeague(roster, playerId);
    if (result.success) {
      setRoster(result.roster);
      setNotification({
        type: 'success',
        message: 'Player moved to minor league'
      });
      setSelectedPlayer(null);
    } else {
      setNotification({
        type: 'error',
        message: result.error
      });
    }
  };

  const handleMoveFromMinorLeague = (playerId) => {
    // Check if roster moves are locked
    if (rosterLockStatus.locked) {
      setNotification({
        type: 'error',
        message: 'Cannot move players - roster is locked'
      });
      return;
    }
    
    const result = movePlayerFromMinorLeague(roster, playerId);
    if (result.success) {
      setRoster(result.roster);
      setNotification({
        type: 'success',
        message: 'Player moved from minor league to bench'
      });
    } else {
      setNotification({
        type: 'error',
        message: result.error
      });
    }
  };

  const handleSelectPlayer = (player) => {
    if (readOnly) return; // no lineup moves when viewing another team
    setSelectedPlayer(player);
    setError('');
    setSuccess('');
  };

  const handleMoveSelectedPlayer = async (destination) => {
    if (!selectedPlayer) return;
    const result = await movePlayerToLineupPosition(roster, selectedPlayer.id, destination);
    if (result.success) {
      setRoster(result.roster);
      setSelectedPlayer(null);
    } else {
      setError(result.error);
    }
  };

  const handleCancelSelection = () => {
    setSelectedPlayer(null);
    setSelectedPosition(null);
    setError('');
    setSuccess('');
  };

  const handlePositionClick = async (position, targetPlayerForArray = null) => {
    if (readOnly) return; // lineup is not editable when viewing another team
    if (rosterLockStatus.locked && rosterLockStatus.lockType === 'full') {
      setNotification({
        type: 'error',
        message: 'Lineup is locked - no changes allowed until Monday'
      });
      return;
    }

    if (selectedPlayer) {
      const result = await movePlayerToLineupPosition(
        roster,
        selectedPlayer.id,
        position,
        userId,
        leagueId,
        currentDate,
        league
      );
      if (result.success) {
        setRoster(result.roster);
        setSelectedPlayer(null);
      } else {
        setError(result.error);
      }
    } else if (!selectedPosition) {
      // First position selected
      setSelectedPosition(position);
      setError('');
      setSuccess('');
    } else if (selectedPosition === position) {
      // Same position clicked - deselect
      setSelectedPosition(null);
    } else {
      // Second position selected - perform swap
      const player1 = getPlayerFromPosition(selectedPosition);
      const player2 = targetPlayerForArray ?? (['P', 'OF', 'UTIL'].includes(position) ? null : roster.activeLineup[position]);
      
      if (!player1 && !player2) {
        // Both positions empty - just deselect
        setSelectedPosition(null);
        return;
      }
      
      if (!player1) {
        // First selected position is empty
        if (player2 && isPositionEligible(player2.position, selectedPosition)) {
          // Move player2 to the empty position
          const result = await movePlayerToLineupPosition(
            roster,
            player2.id,
            selectedPosition,
            userId,
            leagueId,
            currentDate,
            league
          );
          if (result.success) {
            setRoster(result.roster);
            setSelectedPosition(null); // Clear selection after successful move
          } else {
            setError(result.error);
          }
        } else if (player2) {
          // Player not eligible for first position - move to bench
          const result = movePlayerToBench(roster, player2.id);
          if (result.success) {
            setRoster(result.roster);
            setSelectedPosition(null);
          } else {
            setError(result.error);
          }
        }
      } else if (!player2) {
        // Second selected position is empty
        if (isPositionEligible(player1.position, position)) {
          // Move player1 to the empty position
          const result = await movePlayerToLineupPosition(
            roster,
            player1.id,
            position,
            userId,
            leagueId,
            currentDate,
            league
          );
          if (result.success) {
            setRoster(result.roster);
            setSelectedPosition(null); // Clear selection after successful move
          } else {
            setError(result.error);
          }
        } else {
          setError(`${player1.name} is not eligible for ${position}`);
        }
      } else {
        // Both positions have players - swap them
        const player1EligibleForPos2 = isPositionEligible(player1.position, position);
        const player2EligibleForPos1 = isPositionEligible(player2.position, selectedPosition);
        
        if (player1EligibleForPos2 && player2EligibleForPos1) {
          const multiSlot = ['P', 'OF', 'UTIL'];
          const needsMoveApi = multiSlot.includes(selectedPosition) || multiSlot.includes(position);
          if (needsMoveApi) {
            const r1 = await movePlayerToLineupPosition(roster, player1.id, position, userId, leagueId, currentDate, league);
            if (r1.success) {
              const r2 = await movePlayerToLineupPosition(r1.roster, player2.id, selectedPosition, userId, leagueId, currentDate, league);
              if (r2.success) {
                setRoster(r2.roster);
                setSelectedPosition(null);
              } else setError(r2.error);
            } else setError(r1.error);
          } else {
            const newRoster = {
              ...roster,
              activeLineup: { ...roster.activeLineup, [selectedPosition]: player2, [position]: player1 },
              lastUpdated: getCurrentDate()
            };
            setRoster(newRoster);
            setSelectedPosition(null);
            try {
              await updateLineupForAllFutureDates(userId, leagueId, newRoster.activeLineup, currentDate, league);
            } catch (error) {
              console.error('Error updating future dates:', error);
              setNotification({ type: 'error', message: 'Lineup updated but failed to update future dates' });
            }
          }
        } else if (player1EligibleForPos2) {
          const result1 = await movePlayerToLineupPosition(
            roster,
            player1.id,
            position,
            userId,
            leagueId,
            currentDate,
            league
          );
          if (result1.success) {
            const result2 = movePlayerToBench(result1.roster, player2.id);
            if (result2.success) {
              setRoster(result2.roster);
              setSelectedPosition(null);
            } else {
              setError(result2.error);
            }
          } else setError(result1.error);
        } else if (player2EligibleForPos1) {
          const result1 = await movePlayerToLineupPosition(
            roster,
            player2.id,
            selectedPosition,
            userId,
            leagueId,
            currentDate,
            league
          );
          if (result1.success) {
            const result2 = movePlayerToBench(result1.roster, player1.id);
            if (result2.success) {
              setRoster(result2.roster);
              setSelectedPosition(null);
            } else {
              setError(result2.error);
            }
          } else setError(result1.error);
        } else {
          const result1 = movePlayerToBench(roster, player1.id);
          if (result1.success) {
            const result2 = movePlayerToBench(result1.roster, player2.id);
            if (result2.success) {
              setRoster(result2.roster);
              setSelectedPosition(null);
            } else {
              setError(result2.error);
            }
          } else {
            setError(result1.error);
          }
        }
      }
    }
  };

  const handleDateChange = (newDate) => {
    // Constrain date to season bounds
    const constrainedDate = constrainDateToSeason(newDate, leagueSeason);
    setCurrentDate(constrainedDate);
    setError('');
    setSuccess('');
    setSelectedPlayer(null);
    setSelectedPosition(null);
  };

  const handleDateForward = () => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + 1);
    const nextDate = date.toISOString().split('T')[0];
    handleDateChange(nextDate);
  };

  const handleDateBackward = () => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() - 1);
    const prevDate = date.toISOString().split('T')[0];
    handleDateChange(prevDate);
  };

  // Check if we can navigate forward/backward
  const canGoForward = currentDate < leagueSeason.endDate;
  const canGoBackward = currentDate > leagueSeason.startDate;

  // Lineup-period dropdown options (design-system .pselect): every date in the
  // season, so the period control is a select rather than a calendar pill.
  const periodDateOptions = React.useMemo(() => {
    const opts = [];
    const start = new Date(`${leagueSeason.startDate}T00:00:00`);
    const end = new Date(`${leagueSeason.endDate}T00:00:00`);
    const d = new Date(start);
    let guard = 0;
    while (d <= end && guard < 400) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      opts.push({ value: iso, label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) });
      d.setDate(d.getDate() + 1);
      guard += 1;
    }
    return opts;
  }, [leagueSeason.startDate, leagueSeason.endDate]);

  const handleCopyRoster = async () => {
    if (availableDates.length === 0) {
      setError('No previous rosters to copy from');
      return;
    }
    const previousDate = availableDates[availableDates.length - 1];
    const newRoster = await copyRosterToDate(userId, leagueId, previousDate, currentDate, league);
    setRoster(newRoster);
    setSuccess(`Roster copied from ${previousDate}`);
  };

  // Generate mock today's stats
  const generateTodaysStats = (player) => {
    const isHitter = !['P', 'SP', 'RP'].includes(player.position);
    
    if (isHitter) {
      return {
        hits: Math.floor(Math.random() * 5),
        atBats: Math.floor(Math.random() * 5) + 1,
        runs: Math.floor(Math.random() * 4),
        homeRuns: Math.floor(Math.random() * 2),
        rbi: Math.floor(Math.random() * 4),
        stolenBases: Math.floor(Math.random() * 2),
        average: (Math.random() * 0.5 + 0.2).toFixed(3)
      };
    } else {
      return {
        innings: (Math.random() * 9).toFixed(1),
        wins: Math.floor(Math.random() * 2),
        saves: Math.floor(Math.random() * 2),
        strikeouts: Math.floor(Math.random() * 10),
        era: (Math.random() * 5 + 1).toFixed(2),
        whip: (Math.random() * 1.5 + 0.8).toFixed(2)
      };
    }
  };

  // Most recent news item per player, derived from the already-loaded rosterNews feed
  const newsByPlayer = React.useMemo(() => {
    const map = new Map();
    for (const item of rosterNews) {
      const pid = String(item.playerId);
      if (!map.has(pid)) map.set(pid, item);
    }
    return map;
  }, [rosterNews]);
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };
  const newsCellFor = (player) => {
    const item = player ? newsByPlayer.get(String(player.id)) : null;
    return (
      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
        {item ? (
          <div>
            <div style={{ fontSize: '12px', lineHeight: 1.3 }}>{item.Title}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>{timeAgo(item.Updated)} ago</div>
          </div>
        ) : (
          <span style={{ color: 'var(--muted-foreground)', fontSize: '11px' }}>No recent news</span>
        )}
      </td>
    );
  };

  // Separate hitters and pitchers
  const hitters = roster?.players.filter(player => !['P', 'SP', 'RP'].includes(player.position)) || [];
  const pitchers = roster?.players.filter(player => ['P', 'SP', 'RP'].includes(player.position)) || [];
  
  // Get bench players by type
  const benchHitters = roster?.bench.filter(player => !['P', 'SP', 'RP'].includes(player.position)) || [];
  const benchPitchers = roster?.bench.filter(player => ['P', 'SP', 'RP'].includes(player.position)) || [];
  
  // Get minor league players by type and available slots
  const minorLeaguePlayers = roster?.minorLeague || [];
  const minorLeagueHitters = minorLeaguePlayers.filter(player => !['P', 'SP', 'RP'].includes(player.position));
  const minorLeaguePitchers = minorLeaguePlayers.filter(player => ['P', 'SP', 'RP'].includes(player.position));
  const minorLeagueSpots = league?.settings?.rosterSettings?.minorLeagueSpots ?? MAX_MINOR_LEAGUE_SIZE;
  const minorLeagueUnlimited = minorLeagueSpots == null || minorLeagueSpots >= 99;
  const minorLeagueAvailable = minorLeagueUnlimited ? 1 : Math.max(0, minorLeagueSpots - minorLeaguePlayers.length);
  const ilSpots = league?.settings?.rosterSettings?.ilSpots ?? 3;
  const ilUnlimited = ilSpots == null || ilSpots >= 99;
  const ilPlayers = roster?.injuredList || [];
  const ilHitters = ilPlayers.filter(player => !['P', 'SP', 'RP'].includes(player.position));
  const ilPitchers = ilPlayers.filter(player => ['P', 'SP', 'RP'].includes(player.position));
  const ilAvailable = ilUnlimited ? 1 : Math.max(0, ilSpots - ilPlayers.length);
  const benchSpots = league?.settings?.rosterSettings?.benchSpots;
  const benchUnlimited = benchSpots == null;
  const benchAvailable = benchUnlimited ? 1 : Math.max(0, benchSpots - (roster?.bench?.length || 0));

  const positions = [
    'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'P', 'SP', 'RP'
  ];

  const teams = [
    'NYY', 'BOS', 'TB', 'TOR', 'BAL',
    'CLE', 'DET', 'KC', 'MIN', 'CWS',
    'HOU', 'LAA', 'OAK', 'SEA', 'TEX',
    'ATL', 'MIA', 'NYM', 'PHI', 'WSH',
    'CHC', 'CIN', 'MIL', 'PIT', 'STL',
    'ARI', 'COL', 'LAD', 'SD', 'SF'
  ];

  const opponentTeams = React.useMemo(() => {
    if (!league?.teams) return [];
    return (Array.isArray(league.teams) ? league.teams : []).filter(t => t.owner !== userId);
  }, [league?.teams, userId]);

  const openTradeMenu = (player, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTradeMenu({ player, x: rect.left, y: rect.bottom + 4 });
  };

  // Trade affordance behind a player's trade button. On the user's own roster it
  // opens the team-picker menu; on a read-only opponent roster it jumps straight
  // into a trade with that team, pre-selecting the clicked (partner) player.
  const startTradeForPlayer = (player, e) => {
    if (readOnly) {
      setTradeModal({ partnerTeam: viewTeam, initialPartnerPlayers: [player] });
    } else {
      openTradeMenu(player, e);
    }
  };

  const renderActionsTd = (player, rowBgColor) => (
    <td style={{ padding: '4px 2px', textAlign: 'center', backgroundColor: rowBgColor, width: '80px', verticalAlign: 'middle' }}>
      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
        {player && !rosterLockStatus.locked && (
          <button
            type="button"
            className="btn btn-danger btn-icon btn-sm"
            onClick={(e) => { e.stopPropagation(); requestDropPlayer(player); }}
            title="Drop"
            aria-label="Drop player"
          >
            <SubtractIcon size={16} />
          </button>
        )}
        {player && opponentTeams.length > 0 && (
          <button
            type="button"
            className="btn btn-primary btn-icon btn-sm"
            onClick={(e) => { e.stopPropagation(); openTradeMenu(player, e); }}
            title="Trade"
            aria-label="Trade player"
          >
            <SwapIcon size={16} />
          </button>
        )}
      </div>
    </td>
  );

  if (!roster) {
    return <div className="container">Loading...</div>;
  }

  /* ----------------------------------------------------------------------
   * Roster-table (rz-) render helpers. Build the design-system two-column
   * Hitters | Pitchers layout from the live roster, reusing the existing
   * move/swap selection state (selectedPosition / selectedPlayer /
   * isValidSwapTarget / handlePositionClick / handleSelectPlayer) and the
   * add/drop + trade actions. See design-system/roster.html for the target.
   * -------------------------------------------------------------------- */
  const of = getOutfielders(roster.activeLineup || {});
  const util = getUtility(roster.activeLineup || {});
  const pitchersActive = getPitchers(roster.activeLineup || {});

  // Ordered active slots per group. `pos` is the lineup key handlePositionClick expects.
  const hitterActiveSlots = [
    { key: 'C', label: 'C', pos: 'C', player: roster.activeLineup?.C ?? null },
    { key: '1B', label: '1B', pos: '1B', player: roster.activeLineup?.['1B'] ?? null },
    { key: '2B', label: '2B', pos: '2B', player: roster.activeLineup?.['2B'] ?? null },
    { key: '3B', label: '3B', pos: '3B', player: roster.activeLineup?.['3B'] ?? null },
    { key: 'SS', label: 'SS', pos: 'SS', player: roster.activeLineup?.SS ?? null },
    { key: 'MI', label: 'MI', pos: 'MI', player: roster.activeLineup?.MI ?? null },
    { key: 'CI', label: 'CI', pos: 'CI', player: roster.activeLineup?.CI ?? null },
    ...Array.from({ length: 4 }, (_, i) => ({ key: `OF${i}`, label: 'OF', pos: 'OF', player: of[i] || null })),
    ...Array.from({ length: 2 }, (_, i) => ({ key: `UTIL${i}`, label: 'UT', pos: 'UTIL', player: util[i] || null })),
  ];
  const pitcherActiveSlots = Array.from({ length: 9 }, (_, i) => ({ key: `P${i}`, label: 'P', pos: 'P', player: pitchersActive[i] || null }));

  const rzInjuryChip = (status) => {
    if (status === 'DTD') return <span className="rz-inj-chip dtd">DTD</span>;
    if (status === 'IL-10') return <span className="rz-inj-chip il">IL-10</span>;
    if (status === 'IL-60') return <span className="rz-inj-chip il">IL-60</span>;
    if (status === 'MiLB') return <span className="rz-inj-chip milb">MiLB</span>;
    return null;
  };
  // Slot-pill colour variant: reserves are typed by kind, UTIL/P go grey, rest navy.
  const pillVariant = (kind, label) => {
    if (kind === 'bench') return 'bn';
    if (kind === 'il') return 'il';
    if (kind === 'minor') return 'na';
    if (label === 'UT' || label === 'UTIL' || label === 'P') return 'util';
    return '';
  };

  // Inner content of the player cell (avatar · name/meta · actions). Shared by the
  // desktop table cell and the mobile stacked row.
  const rzPlayerInner = (player, group, label) => {
    if (!player) {
      const word = label === 'BN' ? 'bench' : label === 'IL' ? 'IL' : label === 'ML' ? 'minors' : label;
      return (
        <div className="rz-player-cell">
          <span className="rz-avatar empty" />
          <div className="rz-pc-main">
            <div className="rz-pc-name rz-empty-name">Open {word} slot</div>
            <div className="rz-empty-hint">tap the slot or drop a player</div>
          </div>
        </div>
      );
    }
    const elig = (player.positions || [player.position]).filter((p) => p && p !== 'UTIL' && p !== 'P').slice(0, 4).join(', ');
    // Read-only (opponent) view: no drop, but trade is available and pre-targets
    // this team + player.
    const canDrop = !readOnly && !rosterLockStatus.locked;
    const canTrade = readOnly ? Boolean(userTeamId) : opponentTeams.length > 0;
    return (
        <div className="rz-player-cell">
          <button type="button" className={`rz-avatar ${group === 'p' ? 'pitch' : 'hit'}`} onClick={(e) => { e.stopPropagation(); openPlayerProfile(player, leagueId); }} title="Player profile" aria-label={`${player.name} profile`}>
            {(player.photoUrl || player.headshotUrl || player.image)
              ? <img src={player.photoUrl || player.headshotUrl || player.image} alt="" />
              : <PlayerSilhouette />}
          </button>
          <div className="rz-pc-main">
            <div className="rz-pc-name">
              <button type="button" className="rz-name-btn" onClick={(e) => { e.stopPropagation(); openPlayerProfile(player, leagueId); }}>{player.name}</button>
              {rzInjuryChip(player.status)}
            </div>
            <div className="rz-pc-meta">{player.team}{elig && <span className="rz-elig"> &middot; {elig}</span>}</div>
          </div>
          {(canDrop || canTrade) && (
            <div className="rz-actions">
              {canDrop && (
                <button type="button" className="btn btn-danger btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); requestDropPlayer(player); }} title="Drop" aria-label="Drop player">
                  <SubtractIcon size={14} />
                </button>
              )}
              {canTrade && (
                <button type="button" className="btn btn-primary btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); startTradeForPlayer(player, e); }} title="Trade" aria-label="Trade player">
                  <SwapIcon size={14} />
                </button>
              )}
            </div>
          )}
        </div>
    );
  };
  const rzPlayerCell = (player, group, label) => <td className="rz-c-player">{rzPlayerInner(player, group, label)}</td>;

  // Data shown as a horizontal-scroll strip under the player on narrow screens:
  // opponent/game, salary, contract, then the current view's stat columns.
  const rzDataPairs = (player, group) => {
    const pairs = [];
    const gs = player ? getPlayerGameStatus(player, mlbGames) : null;
    const gameText = !gs ? '—'
      : gs.kind === 'pre' ? `${gs.opponent} · ${gs.timeText}`
      : gs.kind === 'final' ? `${gs.opponent} · ${gs.score} F`
      : `${gs.opponent} · ${gs.score} ${gs.inningText}`;
    pairs.push(['Game', gameText]);
    pairs.push(['Sal', player?.salary || '—']);
    pairs.push(['Yrs', player?.contract || '—']);
    const s = player?.stats || {};
    const show = (v) => (v === 0 || v ? v : '—');
    if (viewMode === 'stats') {
      if (group === 'h') {
        pairs.push(['H/AB', s.atBats != null ? `${s.hits ?? 0}/${s.atBats}` : '—'], ['AVG', show(s.battingAverage)], ['HR', show(s.homeRuns)], ['RBI', show(s.rbi)], ['R', show(s.runs)], ['SB', show(s.stolenBases)]);
      } else {
        pairs.push(['ERA', show(s.era)], ['WHIP', show(s.whip)], ['K', show(s.strikeouts)], ['W', show(s.wins)], ['SV', show(s.saves)]);
      }
    } else if (viewMode === 'research' && player && (researchSubTab === 'saber' || researchSubTab === 'statcast')) {
      const vals = researchSubTab === 'saber' ? (group === 'p' ? saberP(player.id) : saberH(player.id)) : (group === 'p' ? statcastP(player.id) : statcastH(player.id));
      const labels = researchSubTab === 'saber' ? saberLabels(group) : statcastLabels(group);
      vals.forEach(([, v], i) => pairs.push([labels[i] || '', v]));
    }
    return pairs;
  };

  // Mobile stacked row body: player details on top, a scrolling data strip beneath.
  const rzMobileBody = (player, group, label) => (
    <td className="rz-mcell" colSpan={99}>
      <div className="rz-mrow">
        {rzPlayerInner(player, group, label)}
        <div className="rz-mdata">
          {rzDataPairs(player, group).map(([l, v]) => (
            <div className="rz-mchip" key={l}>
              <span className="rz-mchip-l">{l}</span>
              <span className="rz-mchip-v">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </td>
  );

  const rzGameCell = (player) => {
    if (!player) return <td className="rz-c-game"><span className="rz-empty-cell">&mdash;</span></td>;
    const g = getPlayerGameStatus(player, mlbGames);
    if (!g) return <td className="rz-c-game"><span className="rz-empty-cell">&mdash;</span></td>;
    const away = g.opponent.startsWith('@');
    // Under the opponent: the start time before first pitch, otherwise the live
    // (or final) score and inning.
    const sub = g.kind === 'pre' ? g.timeText
      : g.kind === 'final' ? `${g.score} · Final`
      : `${g.score} · ${g.inningText}`;
    return (
      <td className="rz-c-game">
        <div className="rz-g-cell">
          <span className={`rz-g-opp${away ? ' away' : ''}`}>{g.opponent}</span>
          <span className="rz-g-sub">{sub}</span>
        </div>
      </td>
    );
  };

  // Per-view trailing cells. Stats use season totals (real); Research/Schedule
  // Deterministic sample research values (ownership % and rolling player ratings).
  const rzMock = (id, key) => {
    const str = String(id) + key;
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
    const x = Math.sin(Math.abs(h) + 1) * 10000;
    return x - Math.floor(x);
  };
  const ownPct = (id) => Math.round(rzMock(id, 'own') * 100);
  const playerRating = (id, key) => Math.round(rzMock(id, key) * 40 + 40); // 40..80 (matches the Players page)
  // Deterministic opponent for a player on a given date (or null on an off day).
  const SCHED_OPP = ['NYY', 'BOS', 'TB', 'TOR', 'BAL', 'CLE', 'DET', 'KC', 'MIN', 'CWS', 'HOU', 'LAA', 'OAK', 'SEA', 'TEX', 'ATL', 'MIA', 'NYM', 'PHI', 'WSH', 'CHC', 'CIN', 'MIL', 'PIT', 'STL', 'ARI', 'COL', 'LAD', 'SD', 'SF'];
  const scheduleOpponent = (id, date) => {
    const key = `sc${date.getMonth()}_${date.getDate()}`;
    if (rzMock(id, key) < 0.18) return null; // off day ~18%
    const opp = SCHED_OPP[Math.floor(rzMock(id, key + 'o') * SCHED_OPP.length)];
    return `${rzMock(id, key + 'h') > 0.5 ? 'vs' : '@'} ${opp}`;
  };
  // Same Baseball-Savant rating badge as the Players page: diverging
  // blue→white→red with saturated extremes, white/near-black text by luminance.
  const ratingHeatColor = (r) => {
    const t = Math.max(0, Math.min(1, (r - 40) / 40));
    const lerp = (a, b, u) => Math.round(a + (b - a) * u);
    let R, G, B;
    if (t < 0.5) { const u = t / 0.5; R = lerp(40, 247, u); G = lerp(94, 247, u); B = lerp(200, 249, u); }
    else { const u = (t - 0.5) / 0.5; R = lerp(247, 205, u); G = lerp(247, 42, u); B = lerp(249, 42, u); }
    const lum = 0.299 * R + 0.587 * G + 0.114 * B;
    return { bg: `rgb(${R}, ${G}, ${B})`, fg: lum > 150 ? '#17150f' : '#ffffff' };
  };
  const ratingCell = (id, key) => {
    const r = playerRating(id, key);
    const c = ratingHeatColor(r);
    return (
      <td className="rz-c-stat rz-num">
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '22px', borderRadius: '6px', background: c.bg, color: c.fg, border: '1px solid rgba(0,0,0,0.08)', fontSize: '12px', fontWeight: 700 }}>{r}</span>
      </td>
    );
  };
  // Ownership sub-category values.
  const ownChange = (id, key) => Math.round((rzMock(id, key) - 0.5) * 30); // -15..+15 pts
  const draftPct = (id) => Math.round(rzMock(id, 'draftpct') * 100);
  const adp = (id) => (rzMock(id, 'adp') * 280 + 1).toFixed(1); // 1.0..281.0
  const deltaCell = (id, key) => {
    const d = ownChange(id, key);
    const color = d > 0 ? 'var(--success)' : d < 0 ? 'var(--danger)' : 'var(--muted)';
    return <td className="rz-c-stat rz-num" style={{ color, fontWeight: 600 }}>{d > 0 ? `+${d}` : d}</td>;
  };
  // Sabermetric values (hitters vs pitchers).
  const saberH = (id) => [
    ['OBP', (0.300 + rzMock(id, 'obp') * 0.12).toFixed(3)],
    ['SLG', (0.350 + rzMock(id, 'slg') * 0.25).toFixed(3)],
    ['OPS', (0.650 + rzMock(id, 'ops') * 0.35).toFixed(3)],
    ['wOBA', (0.290 + rzMock(id, 'woba') * 0.13).toFixed(3)],
    ['wRC+', Math.round(70 + rzMock(id, 'wrc') * 100)],
  ];
  const saberP = (id) => [
    ['FIP', (2.50 + rzMock(id, 'fip') * 3).toFixed(2)],
    ['xFIP', (2.50 + rzMock(id, 'xfip') * 3).toFixed(2)],
    ['K/9', (6 + rzMock(id, 'k9') * 7).toFixed(1)],
    ['BB/9', (1.5 + rzMock(id, 'bb9') * 3.5).toFixed(1)],
    ['HR/9', (0.6 + rzMock(id, 'hr9') * 1.4).toFixed(1)],
  ];
  const saberLabels = (group) => (group === 'p' ? ['FIP', 'xFIP', 'K/9', 'BB/9', 'HR/9'] : ['OBP', 'SLG', 'OPS', 'wOBA', 'wRC+']);
  // Statcast metrics (hitters vs pitchers).
  const statcastH = (id) => [
    ['EV', (85 + rzMock(id, 'ev') * 10).toFixed(1)],
    ['Barrel%', (3 + rzMock(id, 'barrel') * 15).toFixed(1)],
    ['HardHit%', (30 + rzMock(id, 'hardhit') * 25).toFixed(1)],
    ['xwOBA', (0.290 + rzMock(id, 'xwoba') * 0.13).toFixed(3)],
    ['Sprint', (24 + rzMock(id, 'sprint') * 6).toFixed(1)],
  ];
  const statcastP = (id) => [
    ['Velo', (90 + rzMock(id, 'velo') * 9).toFixed(1)],
    ['Spin', Math.round(2100 + rzMock(id, 'spin') * 500)],
    ['Whiff%', (18 + rzMock(id, 'whiff') * 20).toFixed(1)],
    ['Chase%', (22 + rzMock(id, 'chase') * 16).toFixed(1)],
    ['xERA', (2.80 + rzMock(id, 'xera') * 2.4).toFixed(2)],
  ];
  const statcastLabels = (group) => (group === 'p' ? ['Velo', 'Spin', 'Whiff%', 'Chase%', 'xERA'] : ['EV', 'Barrel%', 'HardHit%', 'xwOBA', 'Sprint']);

  // render the design "—" empty-cell where the app has no such datum (no fabrication).
  const rzTrailing = (player, group) => {
    if (!player) {
      if (viewMode === 'schedule') return scheduleDays.map((d, i) => <td key={i} className="rz-c-sched-day"><span className="rz-empty-cell">&mdash;</span></td>);
      const n = viewMode === 'research' ? 5 : (group === 'h' ? 6 : 5);
      return Array.from({ length: n }).map((_, i) => <td key={i} className="rz-c-stat rz-num"><span className="rz-empty-cell">&mdash;</span></td>);
    }
    const s = player.stats || {};
    const show = (v) => (v === 0 || v ? v : '—');
    if (viewMode === 'stats') {
      const hab = s.atBats != null ? `${s.hits ?? 0}/${s.atBats}` : null;
      const cells = group === 'h'
        ? [['hab', hab], ['avg', s.battingAverage, true], ['hr', s.homeRuns], ['rbi', s.rbi], ['r', s.runs], ['sb', s.stolenBases]]
        : [['era', s.era, true], ['whip', s.whip], ['k', s.strikeouts], ['w', s.wins], ['sv', s.saves]];
      return cells.map(([k, v, key]) => <td key={k} className={`rz-c-stat rz-num${key ? ' rz-key' : ''}`}>{show(v)}</td>);
    }
    if (viewMode === 'research') {
      if (researchSubTab === 'ownership') {
        return (
          <>
            <td className="rz-c-stat rz-num">{ownPct(player.id)}%</td>
            {deltaCell(player.id, 'd7')}
            {deltaCell(player.id, 'd14')}
            <td className="rz-c-stat rz-num">{draftPct(player.id)}%</td>
            <td className="rz-c-stat rz-num">{adp(player.id)}</td>
          </>
        );
      }
      if (researchSubTab === 'saber') {
        const vals = group === 'p' ? saberP(player.id) : saberH(player.id);
        return <>{vals.map(([k, v]) => <td key={k} className="rz-c-stat rz-num">{v}</td>)}</>;
      }
      if (researchSubTab === 'statcast') {
        const vals = group === 'p' ? statcastP(player.id) : statcastH(player.id);
        return <>{vals.map(([k, v]) => <td key={k} className="rz-c-stat rz-num">{v}</td>)}</>;
      }
      // Player Rating: rolling (7/14/30/60-day) + overall ratings.
      return (
        <>
          {ratingCell(player.id, '7d')}
          {ratingCell(player.id, '14d')}
          {ratingCell(player.id, '30d')}
          {ratingCell(player.id, '60d')}
          {ratingCell(player.id, 'ovr')}
        </>
      );
    }
    // schedule — one column per date, opponent (vs/@ TEAM) as the value.
    return scheduleDays.map((d, i) => {
      const opp = scheduleOpponent(player.id, d);
      const away = opp && opp.startsWith('@');
      return (
        <td key={i} className="rz-c-sched-day">
          {opp
            ? <span className={`rz-g-opp${away ? ' away' : ''}`} style={{ fontSize: '12px' }}>{opp}</span>
            : <span className="rz-empty-cell">&mdash;</span>}
        </td>
      );
    });
  };

  const theadFor = (group) => {
    let statTh;
    if (viewMode === 'stats') {
      const labels = group === 'h' ? ['H/AB', 'AVG', 'HR', 'RBI', 'R', 'SB'] : ['ERA', 'WHIP', 'K', 'W', 'SV'];
      statTh = labels.map((l) => <th key={l} className="rz-c-stat rz-num">{l}</th>);
    } else if (viewMode === 'research') {
      const labels = researchSubTab === 'ownership'
        ? ['Own%', 'Δ7D', 'Δ14D', 'Draft%', 'ADP']
        : researchSubTab === 'saber'
          ? saberLabels(group)
          : researchSubTab === 'statcast'
            ? statcastLabels(group)
            : ['7D', '14D', '30D', '60D', 'Ovr'];
      statTh = labels.map((l) => <th key={l} className="rz-c-stat rz-num">{l}</th>);
    } else {
      // schedule — a column per date; the header is the weekday + date on one line.
      statTh = scheduleDays.map((d, i) => (
        <th key={i} className="rz-c-sched-day">
          <div style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
            <span style={{ color: 'var(--muted)', fontWeight: 600 }}>{d.toLocaleDateString('en-US', { weekday: 'short' })}</span> {d.getMonth() + 1}/{d.getDate()}
          </div>
        </th>
      ));
    }
    return (
      <thead>
        <tr>
          <th className="rz-c-slot">Slot</th>
          <th className="rz-c-player">Player</th>
          <th className="rz-c-game">Game</th>
          <th className="rz-c-sal rz-num">Salary</th>
          <th className="rz-c-con">Contract</th>
          {statTh}
        </tr>
      </thead>
    );
  };

  const renderActiveRow = (slot, group) => {
    const { key, label, pos, player } = slot;
    const isSelected = ['OF', 'UTIL', 'P'].includes(pos)
      ? (selectedPosition === pos && selectedPlayer?.id === player?.id)
      : selectedPosition === pos;
    const validTarget = !isSelected && (
      (Boolean(selectedPosition) && isValidSwapTarget(pos, player || undefined)) ||
      (Boolean(selectedPlayer) && !selectedPosition && isPositionEligible(selectedPlayer.position, pos))
    );
    const hasSelection = Boolean(selectedPosition || selectedPlayer);
    const badTarget = hasSelection && !isSelected && !validTarget;
    const pillState = isSelected ? 'active' : validTarget ? 'target' : badTarget ? 'badtarget' : '';
    const onSlot = () => {
      if (isSelected) { setSelectedPlayer(null); setSelectedPosition(null); return; }
      if (player) {
        if (selectedPosition && selectedPosition !== pos) handlePositionClick(pos, player);
        else if (selectedPlayer && !selectedPosition) handlePositionClick(pos, player);
        else { setSelectedPlayer(player); setSelectedPosition(pos); setError(''); setSuccess(''); }
      } else {
        handlePositionClick(pos);
      }
    };
    const draggable = Boolean(player) && !rosterLockStatus.locked;
    return (
      <tr
        key={key}
        className={`rz-row${player ? ' rz-filled' : ''}${validTarget ? ' rz-target' : ''}`}
        draggable={draggable}
        onDragStart={() => { if (draggable && !hasSelection) onSlot(); }}
        onDragOver={(e) => { if (hasSelection && validTarget) e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); if (validTarget) onSlot(); }}
      >
        <td className="rz-c-slot">
          <span className={`rz-slot-pos ${pillVariant('active', label)}${pillState ? ' ' + pillState : ''}`} onClick={(e) => { e.stopPropagation(); if (!badTarget) onSlot(); }} role="button" tabIndex={0}>{label}</span>
        </td>
        {isNarrowRoster ? rzMobileBody(player, group, label) : (
          <>
            {rzPlayerCell(player, group, label)}
            {rzGameCell(player)}
            <td className="rz-c-sal rz-num">{player?.salary || '—'}</td>
            <td className="rz-c-con">{player?.contract || '—'}</td>
            {rzTrailing(player, group)}
          </>
        )}
      </tr>
    );
  };

  const renderReservePlayerRow = (player, kind, label, group) => {
    const isSel = Boolean(selectedPlayer) && selectedPlayer.id === player.id && !selectedPosition;
    const draggable = !rosterLockStatus.locked;
    const onPill = () => {
      if (isSel) { setSelectedPlayer(null); setSelectedPosition(null); } else { handleSelectPlayer(player); }
    };
    return (
      <tr
        key={`${kind}-${player.id}`}
        className="rz-row rz-filled"
        draggable={draggable}
        onDragStart={() => { if (draggable && !selectedPlayer && !selectedPosition) handleSelectPlayer(player); }}
      >
        <td className="rz-c-slot">
          <span className={`rz-slot-pos ${pillVariant(kind, label)}${isSel ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); onPill(); }} role="button" tabIndex={0}>{label}</span>
        </td>
        {isNarrowRoster ? rzMobileBody(player, group, label) : (
          <>
            {rzPlayerCell(player, group, label)}
            {rzGameCell(player)}
            <td className="rz-c-sal rz-num">{player.salary || '—'}</td>
            <td className="rz-c-con">{player.contract || '—'}</td>
            {rzTrailing(player, group)}
          </>
        )}
      </tr>
    );
  };

  const renderOpenReserveRow = (kind, label, group) => {
    const active = kind === 'bench' ? Boolean(selectedPosition || selectedPlayer) : Boolean(selectedPosition);
    const doMove = () => {
      if (!active) return;
      if (kind === 'bench') {
        const p = ['P', 'OF', 'UTIL'].includes(selectedPosition) ? selectedPlayer : (selectedPosition ? roster.activeLineup[selectedPosition] : selectedPlayer);
        if (p) { const r = movePlayerToBench(roster, p.id); if (r.success) { setRoster(r.roster); setSelectedPosition(null); setSelectedPlayer(null); } else setError(r.error); }
      } else if (kind === 'minor') {
        const p = selectedPosition ? roster.activeLineup[selectedPosition] : null;
        if (p) { const r = movePlayerToMinorLeague(roster, p.id); if (r.success) { setRoster(r.roster); setSelectedPosition(null); } else setError(r.error); }
      } else {
        setNotification({ type: 'error', message: 'IL functionality coming soon' });
      }
    };
    return (
      <tr key={`${kind}-open`} className="rz-row rz-open" onClick={active ? doMove : undefined} style={active ? { cursor: 'pointer' } : undefined}>
        <td className="rz-c-slot">
          <span className={`rz-slot-pos ${pillVariant(kind, label)}${active ? ' target' : ''}`} onClick={(e) => { e.stopPropagation(); doMove(); }} role="button" tabIndex={0}>{label}</span>
        </td>
        {isNarrowRoster ? rzMobileBody(null, group, label) : (
          <>
            {rzPlayerCell(null, group, label)}
            {rzGameCell(null)}
            <td className="rz-c-sal rz-num"><span className="rz-empty-cell">—</span></td>
            <td className="rz-c-con"><span className="rz-empty-cell">—</span></td>
            {rzTrailing(null, group)}
          </>
        )}
      </tr>
    );
  };

  const renderGroupCard = (group) => {
    const isH = group === 'h';
    const activeSlots = isH ? hitterActiveSlots : pitcherActiveSlots;
    const bench = isH ? benchHitters : benchPitchers;
    const il = isH ? ilHitters : ilPitchers;
    const minors = isH ? minorLeagueHitters : minorLeaguePitchers;
    const filledCount = activeSlots.filter((sl) => sl.player).length + bench.length + il.length + minors.length;

    const reserveRows = [];
    if (bench.length > 0) bench.forEach((p) => reserveRows.push(renderReservePlayerRow(p, 'bench', 'BN', group)));
    else if (benchAvailable > 0 || selectedPosition || selectedPlayer) reserveRows.push(renderOpenReserveRow('bench', 'BN', group));
    if (il.length > 0) il.forEach((p) => reserveRows.push(renderReservePlayerRow(p, 'il', 'IL', group)));
    else if (ilAvailable > 0 || selectedPosition) reserveRows.push(renderOpenReserveRow('il', 'IL', group));
    if (minors.length > 0) minors.forEach((p) => reserveRows.push(renderReservePlayerRow(p, 'minor', 'ML', group)));
    else if (minorLeagueAvailable > 0 || selectedPosition) reserveRows.push(renderOpenReserveRow('minor', 'ML', group));

    return (
      <div className="rz-card">
        <div className="rz-card-head">
          <h2>{isH ? 'Hitters' : 'Pitchers'}</h2>
          <span className="rz-count">{filledCount} players</span>
        </div>
        <div className={`rz-tbl-wrap${viewMode === 'schedule' && scheduleSubTab === 'full' ? ' rz-sched-scroll' : ''}`}>
          <table className={`rz-tbl${isNarrowRoster ? ' rz-tbl-stacked' : ''}`}>
            {!isNarrowRoster && theadFor(group)}
            <tbody>
              {activeSlots.map((sl) => renderActiveRow(sl, group))}
              {reserveRows}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={readOnly ? 'rz-readonly' : undefined}>
      {/* The season-phase test-data selector lives in the always-present global
          banner above the navbar (see GlobalTestDataBanner). */}

      {/* Sliding Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 2000,
          backgroundColor: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${notification.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '8px',
          padding: '15px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '300px',
          maxWidth: '400px',
          animation: 'slideInRight 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>{notification.type === 'success' ? '✓' : '⚠'}</span>
          <span style={{ fontWeight: '500' }}>{notification.message}</span>
        </div>
      )}
      
      {/* Roster Lock Warning */}
      {rosterLockStatus.locked && (
        <div className="roster-lock-banner">
          <span className="lock-tag"><span className="ld" /></span>
          <div style={{ flex: 1 }}>
            <h3>{rosterLockStatus.lockType === 'full' ? 'Roster Locked' : 'Roster Adds/Drops Locked'}</h3>
            <p>{rosterLockStatus.reason}</p>
            {rosterLockStatus.lockType === 'roster-only' && (
              <p>You can still adjust your active lineup, but cannot add or drop players until Monday.</p>
            )}
            {rosterLockStatus.lockType === 'full' && (
              <p>Your roster and lineup are locked for this week. No changes can be made until Monday.</p>
            )}
          </div>
        </div>
      )}
      
      {/* Left column: franchise, roster period, filters, tables. Right column: news when wide enough */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: 0 }}>
        {/* Content grows to 1200 while the news rail stays 320 (content shrinks
            first on narrower screens); past that reference width the two share
            extra space in a 1200:320 (=3.75:1) ratio, so they scale proportionally. */}
        <div style={{ flex: '3.75 1 1200px', minWidth: 0 }}>
      {/* Franchise card */}
      {viewTeam && (
        <div className="card" style={{ marginBottom: '16px', boxShadow: 'none' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', justifyContent: 'space-between', gap: '20px' }}>
            {/* Franchise */}
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '12px', color: 'var(--muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Franchise</div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                {/* Square logo sized (via the franchiseTextRef measurement) to
                    match the height of the name / owner / buttons column. */}
                <TeamLogo team={viewTeam} size={franchiseLogoSize} />
                <div ref={franchiseTextRef} style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '5px' }}>
                  {/* Row 1: team name + record + place */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', lineHeight: 1.2 }}>
                    <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 'bold', color: 'var(--foreground)' }}>{viewTeam.name}</h3>
                    {userTeamStanding && userTeamStanding.isHeadToHead && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border-strong)', color: 'var(--foreground)' }}>
                        {userTeamStanding.wins}–{userTeamStanding.losses}{userTeamStanding.ties > 0 ? `–${userTeamStanding.ties}` : ''}
                      </span>
                    )}
                    {userTeamStanding && userTeamStanding.isRotisserie && !userTeamStanding.isHeadToHead && (
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border-strong)', color: 'var(--foreground)' }}>{userTeamStanding.rotoPoints ?? '—'} roto pts</span>
                    )}
                    {userTeamStanding && userTeamStanding.isPointsBased && !userTeamStanding.isHeadToHead && (
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border-strong)', color: 'var(--foreground)' }}>{userTeamStanding.points ?? '—'} pts</span>
                    )}
                    {userTeamStanding && userTeamStanding.rank > 0 && (
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#add8e6', color: '#0d2137' }}>
                        {userTeamStanding.rank}{userTeamStanding.rank === 1 ? 'st' : userTeamStanding.rank === 2 ? 'nd' : userTeamStanding.rank === 3 ? 'rd' : 'th'}
                      </span>
                    )}
                  </div>
                  {/* Row 2: owner + trophies */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', color: 'var(--foreground)' }}>{viewTeam.ownerName || 'Unknown Owner'}</span>
                    {(() => {
                      const leagueSettings = typeof league?.settings === 'string' ? JSON.parse(league.settings || '{}') : (league?.settings || {});
                      const history = leagueSettings.history || [];
                      const userEmail = user?.email || '';
                      const userFullName = user?.fullName || '';
                      const userName = user?.name || '';
                      const userTeamName = (viewTeam.name || '').toLowerCase();
                      const isUserTeam = (owner, teamName) =>
                        owner === userEmail || owner === userFullName || owner === userName || owner === userId ||
                        (teamName && userTeamName && String(teamName).toLowerCase() === userTeamName);
                      const placements = [];
                      history.forEach((season) => {
                        if (isUserTeam(season.champion, season.championTeamName)) placements.push({ place: 1, year: season.year });
                        else if (isUserTeam(season.runnerUp, season.runnerUpTeamName)) placements.push({ place: 2, year: season.year });
                        else if (isUserTeam(season.thirdPlace, season.thirdPlaceTeamName)) placements.push({ place: 3, year: season.year });
                      });
                      if (placements.length === 0) return null;
                      const placeColors = { 1: '#d4af37', 2: '#c0c0c0', 3: '#cd7f32' };
                      const placeLabels = { 1: '1st', 2: '2nd', 3: '3rd' };
                      return (
                        <div style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
                          {placements.map((p) => (
                            <span key={`${p.place}-${p.year}`} className="rz-trophy" data-tip={`${p.year} · ${placeLabels[p.place]}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', color: placeColors[p.place] || '#666', cursor: 'default' }} title={`${p.year} season – ${placeLabels[p.place]} place`}>
                              <TrophyIcon size={14} />
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Row 3: settings + history, below the owner / record / place */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                    {onOpenTeamSettings && (
                      <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onOpenTeamSettings} title="Team settings" aria-label="Team settings">
                        <CogIcon size={16} />
                      </button>
                    )}
                    <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate(`/league/${leagueId}/history`)} title="League history" aria-label="League history">
                      <HistoryIcon size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Matchup — for the viewed team (the logged-in user on My
                Roster, or the opponent on a team page). */}
            {phase !== 'pre-draft' && viewMatchup && viewMatchup.categoryResults && viewMatchup.categoryResults.length > 0 && (
              <div className="roster-current-matchup" style={{ flex: '1 1 320px', minWidth: 0, overflowX: 'auto' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '12px', color: 'var(--muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Current Matchup</div>
                <table className="roster-style-table matchup-stats-table" style={{ fontFamily: 'var(--font-display)', fontSize: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0 4px 5px 4px', textAlign: 'left', fontWeight: '600', color: 'var(--foreground)', width: '1%' }}>
                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--foreground)' }}>{viewMatchup.team1Wins}–{viewMatchup.team2Wins}{viewMatchup.ties > 0 ? `–${viewMatchup.ties}` : ''}</div>
                      </th>
                      {viewMatchup.categoryResults.map((r, i) => (
                        <th key={i} style={{ padding: '0 4px 5px 4px', textAlign: 'center', fontWeight: '600', color: 'var(--foreground)' }}>
                          <div style={{ fontSize: '10px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{r.category.label}</div>
                        </th>
                      ))}
                      <th style={{ padding: '0 4px 5px 4px', textAlign: 'center', fontWeight: '600', color: 'var(--foreground)' }}>
                        <div className="matchup-stats-header" style={{ fontSize: '10px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GS</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const viewIsTeam1 = (viewMatchup.team1.id || viewMatchup.team1.teamId) === viewTeamId;
                      const viewRowValues = viewIsTeam1 ? viewMatchup.categoryResults.map(r => r.team1Value) : viewMatchup.categoryResults.map(r => r.team2Value);
                      const oppRowValues = viewIsTeam1 ? viewMatchup.categoryResults.map(r => r.team2Value) : viewMatchup.categoryResults.map(r => r.team1Value);
                      const oppTeam = viewIsTeam1 ? viewMatchup.team2 : viewMatchup.team1;
                      return (
                        <>
                          <tr style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '6px 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <TeamLogo team={viewTeam} size={24} />
                              <span style={{ fontWeight: '500', color: 'var(--foreground)' }}>{viewTeam.acronym || viewTeam.name?.substring(0, 4).toUpperCase() || 'Team'}</span>
                            </td>
                            {viewRowValues.map((val, i) => {
                              const categoryResult = viewMatchup.categoryResults[i];
                              const winner = categoryResult?.winner || 'tie';
                              const isViewWinner = (viewIsTeam1 && winner === 'team1') || (!viewIsTeam1 && winner === 'team2');
                              return <td key={i} style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--foreground)', fontWeight: isViewWinner ? 'bold' : 'normal' }}>{val}</td>;
                            })}
                            <td style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--foreground)' }}>0/12</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <TeamLogo team={oppTeam} size={24} />
                              <span style={{ fontWeight: '500', color: 'var(--foreground)' }}>{oppTeam.acronym || oppTeam.name?.substring(0, 4).toUpperCase() || 'Opp'}</span>
                            </td>
                            {oppRowValues.map((val, i) => {
                              const categoryResult = viewMatchup.categoryResults[i];
                              const winner = categoryResult?.winner || 'tie';
                              const isOppWinner = (viewIsTeam1 && winner === 'team2') || (!viewIsTeam1 && winner === 'team1');
                              return <td key={i} style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--foreground)', fontWeight: isOppWinner ? 'bold' : 'normal' }}>{val}</td>;
                            })}
                            <td style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--foreground)' }}>0/12</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      )}
      
      {/* Roster period, filters, and tables - tabs flush to table */}
      <div style={{ width: '100%' }}>
        {/* rz- toolbar: date/period stepper (left) · view segment (center) · lineup-lock tag (right) */}
        <div className="rz-toolbar">
          <div className="rz-period">
            <button type="button" className="rz-pstep" onClick={handleDateBackward} disabled={!canGoBackward} title="Previous day" aria-label="Previous day">&#8249;</button>
            <div className="rz-pselect">
              <select value={currentDate} onChange={(e) => handleDateChange(e.target.value)} aria-label="Lineup period">
                {periodDateOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
              <span className="rz-pcaret">&#9662;</span>
            </div>
            <button type="button" className="rz-pstep" onClick={handleDateForward} disabled={!canGoForward} title="Next day" aria-label="Next day">&#8250;</button>
          </div>

          <div className="rz-viewgroup">
            <div className="rz-seg" role="tablist">
              {[{ id: 'stats', label: 'Stats' }, { id: 'research', label: 'Research' }, { id: 'schedule', label: 'Schedule' }].map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={viewMode === t.id} className={viewMode === t.id ? 'on' : ''} onClick={() => setViewMode(t.id)}>{t.label}</button>
              ))}
              {viewMode === 'stats' && (
                <select className="rz-subselect" aria-label="Stat period" value={statsRange === 'lastyear' ? `year${(leagueSeason.year || 2026) - 1}` : statsRange} onChange={(e) => setStatsRange(e.target.value)}>
                  {[{ value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' }, { value: '7days', label: '7 Days' }, { value: '15days', label: '15 Days' }, { value: '30days', label: '30 Days' }, { value: '60days', label: '60 Days' }, { value: 'ytd', label: 'YTD' }, ...Array.from({ length: 5 }, (_, i) => { const y = (leagueSeason.year || 2026) - 1 - i; return { value: `year${y}`, label: String(y) }; })].map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              )}
              {viewMode === 'research' && (
                <select className="rz-subselect" aria-label="Research category" value={researchSubTab} onChange={(e) => setResearchSubTab(e.target.value)}>
                  {[{ value: 'rating', label: 'Player Rating' }, { value: 'ownership', label: 'Ownership' }, { value: 'saber', label: 'Sabermetrics' }, { value: 'statcast', label: 'Statcast' }].map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                </select>
              )}
              {viewMode === 'schedule' && (
                <select className="rz-subselect" aria-label="Schedule range" value={scheduleSubTab} onChange={(e) => setScheduleSubTab(e.target.value)}>
                  {[{ value: 'week', label: 'Week' }, { value: 'full', label: 'Full' }].map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                </select>
              )}
            </div>
          </div>

          <span className="rz-lock-tag">
            <span className="rz-ld" />
            {rosterLockStatus.locked ? (rosterLockStatus.lockType === 'full' ? 'Roster locked' : 'Adds/drops locked') : 'Lineup unlocked'}
          </span>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '12px' }}>{error}</div>}
        {success && <div className="success-message" style={{ marginBottom: '12px' }}>{success}</div>}

        {/* Two-column roster: Hitters | Pitchers (collapses to one column under 1200px) */}
        <div className="rz-cols">
          {renderGroupCard('h')}
          {renderGroupCard('p')}
        </div>
      </div>
      </div>
      {/* News card - right rail: sticky below the nav stack (navbar 52 + slim games
          banner 34 + league sub-header 48 = 134px) with its own internal scroll,
          independent of the roster list's page scroll. */}
      {userTeam && showPlayerNews && (
        <div style={{ flex: '1 0 320px', minWidth: 0, position: 'sticky', top: 'calc(140px / var(--desktop-zoom, 1))', alignSelf: 'flex-start', maxHeight: 'calc((100vh - 152px) / var(--desktop-zoom, 1))', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="card news-rail-card" style={{ marginBottom: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div className="news-rail-title">Player News</div>
            {rosterNewsLoading ? (
              <div className="news-rail-empty">Loading news…</div>
            ) : rosterNews.length === 0 ? (
              <div className="news-rail-empty">No news for your roster</div>
            ) : (
              <div className="news-rail-list">
                {rosterNews.map((item) => {
                  const isOpen = expandedNewsId === item.id;
                  const meta = [item.playerPos, item.playerTeam].filter(Boolean).join(' · ');
                  const initials = (item.playerName || '')
                    .replace(/[^A-Za-z'\- ]/g, '').split(/\s+/).filter(Boolean)
                    .map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
                  return (
                    <div key={item.id} className={`news-item${isOpen ? ' open' : ''}`}>
                      <button
                        type="button"
                        className="news-head"
                        onClick={() => setExpandedNewsId(isOpen ? null : item.id)}
                        aria-expanded={isOpen}
                      >
                        <span className="news-avatar">{initials}</span>
                        <span className="news-main">
                          <span className="news-top">
                            <span className="news-name">{item.playerName}</span>
                            {meta && <span className="news-team">{meta}</span>}
                            {item.tag && <span className={`news-tag ${item.tag}`}>{NEWS_TAG_LABEL[item.tag] || item.tag}</span>}
                            <span className="news-ago">{timeAgo(item.Updated)} ago</span>
                          </span>
                          <span className="news-headline">{item.Title}</span>
                        </span>
                        <span className="news-chev">›</span>
                      </button>
                      <div className="news-body">
                        <div className="news-body-inner">
                          <div className="news-article">{item.Content}</div>
                          {(item.Source || item.Updated) && (
                            <div className="news-source">
                              — {item.Source && <b>{item.Source}</b>}
                              {item.Updated ? ` · ${new Date(item.Updated).toLocaleDateString()}` : ''}
                            </div>
                          )}
                          {item.Url && (
                            <a href={item.Url} target="_blank" rel="noopener noreferrer" className="news-readmore">Read more →</a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>

      {/* Trade partner picker */}
      {tradeMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setTradeMenu(null)} />
          <div style={{
            position: 'fixed',
            top: Math.min(tradeMenu.y, window.innerHeight - 260),
            left: Math.min(tradeMenu.x, window.innerWidth - 224),
            zIndex: 9999,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '14px',
            boxShadow: 'var(--shadow-xl)',
            padding: '6px',
            minWidth: '200px',
          }}>
            <div style={{ padding: '8px 12px 4px', fontSize: '11px', color: 'var(--muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Select trade partner
            </div>
            {opponentTeams.map((team, idx) => (
              <button
                key={team.id ?? team.teamId ?? idx}
                type="button"
                onClick={() => { setTradeModal({ partnerTeam: team, initialMyPlayers: tradeMenu.player ? [tradeMenu.player] : [] }); setTradeMenu(null); }}
                style={{ display: 'block', width: '100%', padding: '10px 12px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', borderRadius: '8px', fontSize: '14px', color: 'var(--foreground)', letterSpacing: '-0.022em', fontFamily: 'var(--font-family)', transition: 'background 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-muted)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {team.name}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Trade modal */}
      {tradeModal && (
        <TradeModal
          myTeam={userTeam}
          myRoster={roster}
          partnerTeam={tradeModal.partnerTeam}
          league={league}
          leagueId={leagueId}
          userId={userId}
          currentDate={currentDate}
          initialMyPlayers={tradeModal.initialMyPlayers || []}
          initialPartnerPlayers={tradeModal.initialPartnerPlayers || []}
          onClose={() => setTradeModal(null)}
          onSubmit={(proposal) => {
            setTradeModal(null);
            setNotification({ type: 'success', message: `Trade proposal sent to ${proposal.partnerTeam.name}!` });
          }}
        />
      )}

      {/* Floating drop-confirmation bar */}
      {pendingDrop && (
        <div className="rz-dropbar" role="dialog" aria-modal="false" aria-label="Confirm drop">
          <div className="rz-dropbar-msg">
            Drop <strong>{pendingDrop.name}</strong>
            {pendingDrop.position ? ` · ${pendingDrop.position}` : ''} from your roster?
          </div>
          <div className="rz-dropbar-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPendingDrop(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger btn-sm" onClick={confirmPendingDrop}>
              Drop
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={dropAndAddReplacement}>
              Drop &amp; Add Replacement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RosterTables;
